import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Marquee from 'react-fast-marquee';
import axios from 'axios';
import EventCard from './EventCard';
import { API } from '../lib/api';

const FeaturedEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sortSoonestFirst = (items = []) => [...items].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    const normalizeSeriesKey = (event) => `${event.title || ''}`.trim().toLowerCase();
    const buildHomepageEventList = (items = [], targetCount = 24, maxPerSeries = 2) => {
      const selected = [];
      const counts = new Map();

      for (const event of sortSoonestFirst(items)) {
        const key = normalizeSeriesKey(event) || event.id;
        const currentCount = counts.get(key) || 0;

        if (currentCount >= maxPerSeries) continue;

        selected.push(event);
        counts.set(key, currentCount + 1);

        if (selected.length >= targetCount) break;
      }

      return selected;
    };

    const fetchEvents = async () => {
      try {
        const upcomingResponse = await axios.get(`${API}/events`);
        const upcoming = Array.isArray(upcomingResponse.data) ? buildHomepageEventList(upcomingResponse.data) : [];
        setEvents(upcoming);
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <section data-testid="featured-events-section" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
            Events you won't want to miss in Baltimore and beyond.
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Whether you're looking for things to do in Baltimore this weekend, this month or next year, 
            we don't want you to miss a thing. Check out some of our favorite local events!
          </p>
        </div>

        {/* Horizontal Scrolling Events with Marquee */}
        {loading ? (
          <div className="flex gap-6 overflow-x-auto pb-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-80 bg-zinc-900 rounded-xl overflow-hidden animate-pulse">
                <div className="h-48 bg-zinc-800" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-zinc-800 rounded w-3/4" />
                  <div className="h-4 bg-zinc-800 rounded w-1/2" />
                  <div className="h-4 bg-zinc-800 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length > 0 ? (
          <Marquee
            gradient={true}
            gradientColor="#000000"
            gradientWidth={50}
            speed={30}
            pauseOnHover={true}
            direction="left"
          >
            {events.map((event, index) => (
              <div key={event.id} className="mx-3 w-80">
                <EventCard event={event} index={index} />
              </div>
            ))}
          </Marquee>
        ) : (
          <div className="text-center py-12 border border-zinc-800 rounded-2xl bg-zinc-950">
            <h3 className="text-xl font-semibold text-white mb-2">No upcoming events right now</h3>
            <p className="text-zinc-400">Check back soon for new Baltimore events.</p>
          </div>
        )}

        {/* View All & Submit Event Links */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">
          <a
            href="https://events.whatshappeningbaltimore.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-all-events-btn"
            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300"
          >
            View All Events
            <ArrowRight size={20} />
          </a>
          <a
            href="https://events.sanitylounge.com/selection"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="submit-event-btn"
            className="inline-flex items-center gap-2 border border-purple-500 text-purple-500 hover:bg-purple-500 hover:text-white px-8 py-4 rounded-full font-semibold transition-all duration-300"
          >
            Share Your Event with Baltimore!
            <ArrowRight size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturedEvents;
